import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Heart, Stethoscope, Brain, Calendar } from "lucide-react";
import { careTypes } from "../mock";
import { useEnquiry } from "./EnquiryContext";

export const CareTypes = () => {
  const { openEnquiryModal } = useEnquiry();
  
  const icons = {
    "Residential Care": <Heart className="h-8 w-8" />,
    "Nursing Care": <Stethoscope className="h-8 w-8" />,
    "Dementia Care": <Brain className="h-8 w-8" />,
    "End of Life Care": <Heart className="h-8 w-8" />
  };

  return (
    <section id="care-types" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
            Our Services
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Types of Care We Provide
          </h2>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive care services tailored to meet individual needs with compassion and professionalism
          </p>
        </div>

        {/* Care Types Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {careTypes.map((care, index) => (
            <Card
              key={care.id}
              className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={care.image}
                  alt={care.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/90 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-orange-500 text-white p-3 rounded-lg">
                      {icons[care.title]}
                    </div>
                    <h3 className="text-2xl font-bold text-white">{care.title}</h3>
                  </div>
                </div>
              </div>

              <CardContent className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed">{care.description}</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-teal-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Who it's suitable for:
                    </h4>
                    <p className="text-gray-600 text-sm pl-4">{care.suitable}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-teal-700 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      What support is provided:
                    </h4>
                    <p className="text-gray-600 text-sm pl-4">{care.support}</p>
                  </div>
                </div>

                <Button
                  onClick={() => openEnquiryModal("general")}
                  className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid={`care-enquiry-btn-${care.title}`}
                >
                  Enquire About {care.title}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info Card */}
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not Sure Which Care Type You Need?
            </h3>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Our experienced team is here to help. We'll discuss your needs and recommend 
              the most appropriate care package for you or your loved one.
            </p>
            <Button
              onClick={() => openEnquiryModal("speak")}
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="speak-care-team-btn"
            >
              Speak to Our Care Team
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
